import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal } from "lucide-react";

const LOCATIONS = ["All", "Kompally", "Bachupally", "Nagaram", "Uppal", "LB Nagar", "Adibatla", "Shadnagar", "Shamshabad"];
const TYPES = [{ v: "all", l: "All types" }, { v: "apartment", l: "Apartments" }, { v: "villa", l: "Villas" }, { v: "independent house", l: "Independent Houses" }];
const BUDGETS = [
  { v: "any", l: "Any budget" },
  { v: "0-5000000", l: "Under ₹50L" },
  { v: "5000000-8000000", l: "₹50L – ₹80L" },
  { v: "8000000-12000000", l: "₹80L – ₹1.2Cr" },
  { v: "12000000-999999999", l: "₹1.2Cr +" },
];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const filters = {
    type: params.get("type") || "all",
    location: params.get("location") || "All",
    budget: params.get("budget") || "any",
  };

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (filters.type !== "all") query.project_type = filters.type;
    if (filters.location !== "All") query.location = filters.location;
    if (filters.budget !== "any") {
      const [mi, ma] = filters.budget.split("-").map(Number);
      if (!isNaN(mi)) query.min_price = mi;
      if (!isNaN(ma)) query.max_price = ma;
    }
    api.get("/projects", { params: query })
      .then((r) => setProjects(r.data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [filters.type, filters.location, filters.budget]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all" || v === "any" || v === "All") next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return projects;
    const s = q.toLowerCase();
    return projects.filter((p) =>
      [p.name, p.builder_name, p.location].some((v) => (v || "").toLowerCase().includes(s))
    );
  }, [projects, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-2">Discover</div>
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight">Verified projects across Hyderabad</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-xl">Filter by location, budget or type. Every listing here is checked by our team.</p>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border bg-white p-4 md:p-5 flex flex-wrap items-center gap-3 mb-8" data-testid="projects-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="projects-search"
            placeholder="Search project, builder or area"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11"
          />
        </div>
        <Select value={filters.type} onValueChange={(v) => setFilter("type", v)}>
          <SelectTrigger className="w-[160px] h-11" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.location} onValueChange={(v) => setFilter("location", v)}>
          <SelectTrigger className="w-[180px] h-11" data-testid="filter-location"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.budget} onValueChange={(v) => setFilter("budget", v)}>
          <SelectTrigger className="w-[190px] h-11" data-testid="filter-budget"><SelectValue /></SelectTrigger>
          <SelectContent>
            {BUDGETS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="hidden md:inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <SlidersHorizontal className="w-3.5 h-3.5" /> {filtered.length} results
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center text-muted-foreground text-sm">Loading projects…</div>
      ) : filtered.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground text-sm">No projects match your filters.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="projects-grid">
          {filtered.map((p) => <ProjectCard key={p.id} project={p} />)}
        </div>
      )}
    </div>
  );
}
